import { DateTime } from "luxon";

interface GetLongMonthArgs {
	before?: boolean;
	after?: boolean;
}

interface GetLongYearArgs {
	before?: boolean;
	after?: boolean;
}

function getFirstDayOfWeek(): string {
	const currentDate = DateTime.now();
	const firstDay = currentDate.startOf("week");
	return firstDay.toFormat("yyyy-MM-dd");
}

function getLastDayOfWeek(): string {
	const currentDate = DateTime.now();
	const lastDay = currentDate.endOf("week");
	return lastDay.toFormat("yyyy-MM-dd");
}

function getFirstDayOfPreviousWeek(): string {
	const currentDate = DateTime.now();
	const firstDay = currentDate.minus({ week: 1 }).startOf("week");
	return firstDay.toFormat("yyyy-MM-dd");
}

function getLastDayOfPreviousWeek(): string {
	const currentDate = DateTime.now();
	const lastDay = currentDate.minus({ week: 1 }).endOf("week");
	return lastDay.toFormat("yyyy-MM-dd");
}

function getFirstDayOfMonth(): string {
	const currentDate = DateTime.now();
	const firstDay = currentDate.startOf("month");
	return firstDay.toFormat("yyyy-MM-dd");
}

function getLastDayOfMonth(): string {
	const currentDate = DateTime.now();
	const lastDay = currentDate.endOf("month");
	return lastDay.toFormat("yyyy-MM-dd");
}

function getFirstDayOfPreviousMonth(): string {
	const currentDate = DateTime.now();
	const firstDay = currentDate.minus({ month: 1 }).startOf("month");
	return firstDay.toFormat("yyyy-MM-dd");
}

function getLastDayOfPreviousMonth(): string {
	const currentDate = DateTime.now();
	const lastDay = currentDate.minus({ month: 1 }).endOf("month");
	return lastDay.toFormat("yyyy-MM-dd");
}

function getFirstDayOfYear(): string {
	const currentDate = DateTime.now();
	const firstDay = currentDate.startOf("year");
	return firstDay.toFormat("yyyy-MM-dd");
}

function getLastDayOfYear(): string {
	const currentDate = DateTime.now();
	const lastDay = currentDate.endOf("year");
	return lastDay.toFormat("yyyy-MM-dd");
}

function getToday(): string {
	const currentDate = DateTime.now();
	return currentDate.toFormat("yyyy-MM-dd");
}

function getLongMonth(args?: GetLongMonthArgs): string {
	const actualDate = DateTime.now().setLocale("pt-BR");
	const actualMonth = DateTime.now().month;
	if (args?.before) {
		return actualDate.set({ month: actualMonth - 1 }).toLocaleString({
			month: "long"
		});
	}
	if (args?.after) {
		return actualDate.set({ month: actualMonth + 1 }).toLocaleString({
			month: "long"
		});
	}
	return actualDate.toLocaleString({
		month: "long"
	});
}

function getLongYear(args?: GetLongYearArgs): string {
	const actualDate = DateTime.now().setLocale("pt-BR");
	const actualYear = DateTime.now().year;
	if (args?.before) {
		return actualDate.set({ year: actualYear - 1 }).toLocaleString({
			year: "numeric"
		});
	}
	if (args?.after) {
		return actualDate.set({ year: actualYear + 1 }).toLocaleString({
			year: "numeric"
		});
	}
	return actualDate.toLocaleString({
		year: "numeric"
	});
}

function formatDateFromAtlasDatePicker(date: string): string {
	const dateObject = DateTime.fromFormat(date, "dd/MM/yyyy");
	return dateObject.toFormat("yyyy-MM-dd");
}

function isSameDate(
	date1: Date | string | null,
	date2: Date | string | null
): boolean {
	if (!date1 || !date2) {
		return false;
	}
	if (typeof date1 === "string" && typeof date2 === "string") {
		return date1 === date2;
	}
	return (
		(date1 as Date).toISOString().split("T")[0] ===
		(date2 as Date).toISOString().split("T")[0]
	);
}

function isThisWeek(start: Date, end: Date): boolean {
	const startOfWeek = new Date(getFirstDayOfWeek());
	const lastOfWeek = new Date(getLastDayOfWeek());
	return isSameDate(start, startOfWeek) && isSameDate(end, lastOfWeek);
}

function isLastWeek(start: Date, end: Date): boolean {
	const startOfLastWeek = new Date(getFirstDayOfPreviousWeek());
	const lastOfLastWeek = new Date(getLastDayOfPreviousWeek());
	return (
		isSameDate(start, startOfLastWeek) && isSameDate(end, lastOfLastWeek)
	);
}

function isToday(start: Date, end: Date, today: Date): boolean {
	return isSameDate(start, today) && isSameDate(end, today);
}

function isThisMonth(start: Date, end: Date, today: Date): boolean {
	const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
	const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
	return isSameDate(start, startOfMonth) && isSameDate(end, endOfMonth);
}

function isLastMonth(start: Date, end: Date, today: Date): boolean {
	const startOfLastMonth = new Date(
		today.getFullYear(),
		today.getMonth() - 1,
		1
	);
	const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
	return (
		isSameDate(start, startOfLastMonth) && isSameDate(end, endOfLastMonth)
	);
}

function isThisYear(start: Date, end: Date, today: Date): boolean {
	const startOfYear = new Date(today.getFullYear(), 0, 1);
	const endOfYear = new Date(today.getFullYear(), 11, 31);
	return isSameDate(start, startOfYear) && isSameDate(end, endOfYear);
}

function classifyDate(
	startDate: string,
	endDate: string
):
	| "today"
	| "thisWeek"
	| "previousWeek"
	| "thisMonth"
	| "previousMonth"
	| "thisYear"
	| "custom" {
	const today = new Date();
	const start = new Date(startDate);
	const end = new Date(endDate);

	if (isToday(start, end, today)) {
		return "today";
	}

	if (isThisWeek(start, end)) {
		return "thisWeek";
	}

	if (isLastWeek(start, end)) {
		return "previousWeek";
	}

	if (isThisMonth(start, end, today)) {
		return "thisMonth";
	}

	if (isLastMonth(start, end, today)) {
		return "previousMonth";
	}

	if (isThisYear(start, end, today)) {
		return "thisYear";
	}

	return "custom";
}

function formatDateToAtlasDatePickerInput(dateString: string): string {
	const date = new Date(dateString);
	const day = date.getDate().toString().padStart(2, "0");
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
}

function formatDateTimeFromISO(isoString: string): {
	date: string;
	time: string;
} {
	const dt = DateTime.fromISO(isoString);
	return {
		date: dt.toFormat("dd/MM/yyyy"),
		time: dt.toFormat("HH:mm")
	};
}

export {
	getFirstDayOfWeek,
	getLastDayOfWeek,
	getFirstDayOfPreviousWeek,
	getLastDayOfPreviousWeek,
	getFirstDayOfMonth,
	getLastDayOfMonth,
	getFirstDayOfPreviousMonth,
	getLastDayOfPreviousMonth,
	getFirstDayOfYear,
	getLastDayOfYear,
	getToday,
	formatDateFromAtlasDatePicker,
	classifyDate,
	isSameDate,
	getLongMonth,
	getLongYear,
	formatDateToAtlasDatePickerInput,
	formatDateTimeFromISO
};
